import React, { useState, useEffect, useCallback } from 'react';
import { Search, Shield, ShieldAlert, UserX, UserCheck, ChevronLeft, BookOpen, Target, Lightbulb, GitBranch, Trash2 } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import { useAuth } from '../App';

const AdminPage = () => {
  const { dbUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const isAdmin = dbUser?.role === 'admin' || dbUser?.role === 'master_admin';
  const isMasterAdmin = dbUser?.role === 'master_admin';

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      const data = await apiFetch(`/api/admin/users?${params.toString()}`);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (error) {
      if (error?.message?.includes('403') || error?.status === 403) {
        toast.error('You do not have admin access');
      } else {
        toast.error(error?.message || 'Could not load users');
      }
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin, fetchUsers]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-bg-base p-6 lg:p-8 flex items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto mb-4 text-text-tertiary" size={48} />
          <h2 className="font-display text-2xl font-bold text-text-primary mb-2">Access Denied</h2>
          <p className="text-text-secondary">You need admin privileges to view this page.</p>
        </div>
      </div>
    );
  }

  if (selectedUser) {
    return (
      <UserDetail
        userId={selectedUser}
        isMasterAdmin={isMasterAdmin}
        onBack={() => { setSelectedUser(null); fetchUsers(); }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="admin-page">
      <div className="max-w-6xl mx-auto animate-fade-up">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert size={20} className="text-gold" />
              <h1 className="font-display text-4xl font-bold text-text-primary">Admin</h1>
            </div>
            <p className="text-text-secondary">{total} total users</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or username..."
            className="w-full pl-11 pr-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
          />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 bg-bg-surface rounded-2xl border border-border">
            <p className="text-text-secondary">No users found</p>
          </div>
        ) : (
          <div className="bg-bg-surface rounded-2xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-4 py-3 font-medium text-text-secondary">User</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Email</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Role</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Plan</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Status</th>
                  <th className="px-4 py-3 font-medium text-text-secondary">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-border/50 hover:bg-bg-surface-alt/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedUser(u.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-text-primary">{u.name || '—'}</div>
                      <div className="text-xs text-text-tertiary">@{u.username || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-text-secondary">{u.email}</td>
                    <td className="px-4 py-3">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        u.plan === 'pro' ? 'bg-gold-light text-gold' : 'bg-bg-surface-alt text-text-tertiary'
                      }`}>{u.plan || 'free'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {u.disabled ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-error-light text-error">Disabled</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-sage-light text-sage">Active</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-tertiary text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

function RoleBadge({ role }) {
  if (role === 'master_admin') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-light text-amber">Master</span>;
  if (role === 'admin') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-gold-light text-gold">Admin</span>;
  return <span className="px-2 py-0.5 rounded text-xs font-medium bg-bg-surface-alt text-text-tertiary">User</span>;
}

function UserDetail({ userId, isMasterAdmin, onBack }) {
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [goals, setGoals] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('entries');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [u, e, g, l, d] = await Promise.all([
          apiFetch(`/api/admin/users/${userId}`),
          apiFetch(`/api/admin/users/${userId}/entries`),
          apiFetch(`/api/admin/users/${userId}/goals`),
          apiFetch(`/api/admin/users/${userId}/lessons`),
          apiFetch(`/api/admin/users/${userId}/decisions`),
        ]);
        setUser(u.user);
        setEntries(e.entries || []);
        setGoals(g.goals || []);
        setLessons(l.lessons || []);
        setDecisions(d.decisions || []);
      } catch (err) {
        toast.error(err?.message || 'Failed to load user');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const toggleDisable = async () => {
    try {
      const res = await apiFetch(`/api/admin/users/${userId}/disable`, { method: 'PUT' });
      setUser((u) => ({ ...u, disabled: res.disabled }));
      toast.success(res.disabled ? 'User disabled' : 'User re-enabled');
    } catch (err) {
      toast.error(err?.message || 'Failed');
    }
  };

  const setRole = async (role) => {
    try {
      await apiFetch(`/api/admin/users/${userId}/role?role=${role}`, { method: 'PUT' });
      setUser((u) => ({ ...u, role }));
      toast.success(`Role set to ${role}`);
    } catch (err) {
      toast.error(err?.message || 'Failed');
    }
  };

  const setPlan = async (plan) => {
    try {
      await apiFetch(`/api/admin/users/${userId}/plan?plan=${plan}`, { method: 'PUT' });
      setUser((u) => ({ ...u, plan }));
      toast.success(`Plan set to ${plan}`);
    } catch (err) {
      toast.error(err?.message || 'Failed');
    }
  };

  const performDeleteUser = async () => {
    await apiFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
    toast.success('User and all their data permanently deleted');
    onBack();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-base p-6 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-bg-base p-6">
        <button onClick={onBack} className="text-gold hover:text-gold-muted text-sm font-medium flex items-center gap-1">
          <ChevronLeft size={16} /> Back
        </button>
        <p className="text-text-secondary mt-8 text-center">User not found</p>
      </div>
    );
  }

  const tabs = [
    { id: 'entries', label: 'Entries', icon: BookOpen, count: entries.length },
    { id: 'goals', label: 'Goals', icon: Target, count: goals.length },
    { id: 'lessons', label: 'Lessons', icon: Lightbulb, count: lessons.length },
    { id: 'decisions', label: 'Decisions', icon: GitBranch, count: decisions.length },
  ];

  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="admin-user-detail">
      <div className="max-w-5xl mx-auto animate-fade-up">
        <button onClick={onBack} className="text-gold hover:text-gold-muted text-sm font-medium flex items-center gap-1 mb-6">
          <ChevronLeft size={16} /> All users
        </button>

        {/* User card */}
        <div className="bg-bg-surface rounded-2xl p-6 border border-border mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="font-display text-2xl font-bold text-text-primary">{user.name || '—'}</h2>
              <p className="text-text-secondary">@{user.username} · {user.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <RoleBadge role={user.role} />
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  user.plan === 'pro' ? 'bg-gold-light text-gold' : 'bg-bg-surface-alt text-text-tertiary'
                }`}>{user.plan || 'free'}</span>
                {user.disabled && <span className="px-2 py-0.5 rounded text-xs font-medium bg-error-light text-error">Disabled</span>}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Disable toggle */}
              {user.role !== 'master_admin' && (
                <button
                  onClick={toggleDisable}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    user.disabled
                      ? 'bg-sage text-white hover:bg-sage/90'
                      : 'bg-error-light text-error hover:bg-error/20'
                  }`}
                >
                  {user.disabled ? <UserCheck size={16} /> : <UserX size={16} />}
                  {user.disabled ? 'Enable' : 'Disable'}
                </button>
              )}

              {/* Role change (master admin only) */}
              {isMasterAdmin && user.role !== 'master_admin' && (
                <select
                  value={user.role || 'user'}
                  onChange={(e) => setRole(e.target.value)}
                  className="px-3 py-2 rounded-lg text-sm border border-border bg-bg-surface text-text-primary focus:border-gold focus:outline-none"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              )}

              {/* Plan change */}
              <select
                value={user.plan || 'free'}
                onChange={(e) => setPlan(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm border border-border bg-bg-surface text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="free">Free</option>
                <option value="pro">Pro</option>
              </select>

              {/* Delete user — master admin only */}
              {isMasterAdmin && user.role !== 'master_admin' && (
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-error text-white hover:bg-error/90 transition-colors"
                >
                  <Trash2 size={16} />
                  Delete User
                </button>
              )}
            </div>
          </div>
        </div>

        <ConfirmDialog
          open={deleteConfirm}
          onClose={() => setDeleteConfirm(false)}
          title="Permanently delete this user?"
          message={`This will delete ${user.name || user.email}'s account and ALL their data — entries, goals, lessons, decisions, contacts, chapters. This cannot be undone.`}
          detail={`${user.email} (@${user.username})`}
          confirmLabel="Delete everything"
          onConfirm={performDeleteUser}
        />

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                  tab === t.id
                    ? 'bg-gold text-white border-gold'
                    : 'bg-bg-surface text-text-secondary border-border hover:border-gold'
                }`}
              >
                <Icon size={16} />
                {t.label}
                <span className="text-xs opacity-70">({t.count})</span>
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="space-y-3">
          {tab === 'entries' && entries.map((e) => (
            <div key={e.id} className="bg-bg-surface rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-gold capitalize">{e.entry_type}</span>
                <span className="text-xs text-text-tertiary">{e.date}</span>
              </div>
              {e.title && <h4 className="font-medium text-text-primary text-sm">{e.title}</h4>}
              <p className="text-sm text-text-secondary line-clamp-2">{e.body || e.content}</p>
            </div>
          ))}
          {tab === 'goals' && goals.map((g) => (
            <div key={g.id} className="bg-bg-surface rounded-xl p-4 border border-border">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-medium text-text-primary text-sm">{g.title}</h4>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  g.status === 'achieved' ? 'bg-gold-light text-gold' : 'bg-sage-light text-sage'
                }`}>{g.status}</span>
              </div>
              <div className="h-1.5 bg-sage-light/30 rounded-full mt-2">
                <div className="h-full bg-sage rounded-full" style={{ width: `${Math.min(100, g.progress || 0)}%` }} />
              </div>
            </div>
          ))}
          {tab === 'lessons' && lessons.map((l) => (
            <div key={l.id} className="bg-bg-surface rounded-xl p-4 border border-border">
              <p className="text-sm text-text-primary italic">&ldquo;{l.lesson_text}&rdquo;</p>
              <div className="flex gap-2 mt-2">
                <span className="text-xs text-text-tertiary capitalize">{l.category}</span>
                <span className="text-xs text-text-tertiary">{l.date_learned}</span>
              </div>
            </div>
          ))}
          {tab === 'decisions' && decisions.map((d) => (
            <div key={d.id} className="bg-bg-surface rounded-xl p-4 border border-border">
              <h4 className="font-medium text-text-primary text-sm">{d.what_decided}</h4>
              {d.why_decided && <p className="text-sm text-text-secondary mt-1">{d.why_decided}</p>}
              <span className="text-xs text-text-tertiary mt-1 inline-block">{d.decision_date}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
